import React, { useState, useMemo } from 'react';
import { MSForm, FormResponse } from '../Travelbingo';
import { BuyerEntry } from './dataloaders';
import { processBuyerEntries, filterBuyerEntries, BuyerEntryWithLottery } from './ticketUtils';

interface TicketsSichtenProps {
    selectedForm?: MSForm | null;
    formResponses?: FormResponse[];
    onFormResponsesLoaded?: (responses: FormResponse[]) => void;
    buyerData?: BuyerEntry[];
}

const TicketsSichten: React.FC<TicketsSichtenProps> = ({ 
    buyerData = [],
}) => {
    const [searchQuery, setSearchQuery] = useState('');

    // Process buyer data: combine by email and calculate lottery tickets
    const processedEntries = useMemo(() => {
        return processBuyerEntries(buyerData);
    }, [buyerData]);

    // Filter entries based on search query
    const filteredEntries = useMemo(() => {
        return filterBuyerEntries(processedEntries, searchQuery);
    }, [processedEntries, searchQuery]);

    // Calculate totals
    const totals = useMemo(() => {
        const totalPurchased = filteredEntries.reduce((sum, e) => sum + e.ticketsPurchased, 0);
        const totalInLottery = filteredEntries.reduce((sum, e) => sum + e.ticketsInLottery, 0);
        return { totalPurchased, totalInLottery };
    }, [filteredEntries]);

    if (buyerData.length === 0) {
        return (
            <div className="step-content">
                <h2>Tickets sichten</h2>
                <p style={{ color: '#6c757d' }}>
                    Bitte laden Sie zuerst Daten im vorherigen Schritt.
                </p>
            </div>
        );
    }

    return (
        <div className="step-content">
            <h2>Tickets sichten</h2>
            <p>Hier können die Käuferdaten gesichtet werden. Die Einträge werden nach E-Mail-Adresse zusammengefasst.</p>
            
            {/* Info box about bonus rules */}
            <div style={{ 
                marginBottom: '20px', 
                padding: '12px', 
                backgroundColor: '#e7f3ff', 
                borderRadius: '4px',
                border: '1px solid #b8daff'
            }}>
                <strong>💡 Bonus-Regeln für den Lostopf:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Beim Kauf von 10 Losen erhaltet Ihr 2 Lose zusätzlich geschenkt (12 für 50 Euro)!</li>
                    <li>Beim Kauf von 20 Losen erhaltet Ihr 5 weitere Lose zusätzlich geschenkt (25 für 100 Euro)!</li>
                </ul>
            </div>

            {/* Search input */}
            <div style={{ marginBottom: '20px' }}>
                <label htmlFor="search-input" style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: 'bold' 
                }}>
                    🔍 Suche nach E-Mail oder Name:
                </label>
                <input
                    id="search-input"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Suchbegriff eingeben..."
                    style={{
                        padding: '10px 12px',
                        fontSize: '14px',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        width: '100%',
                        maxWidth: '400px',
                        boxSizing: 'border-box'
                    }}
                />
                {searchQuery && (
                    <button
                        onClick={() => setSearchQuery('')}
                        style={{
                            marginLeft: '8px',
                            padding: '10px 16px',
                            fontSize: '14px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Zurücksetzen
                    </button>
                )}
            </div>

            {/* Summary */}
            <div style={{ 
                marginBottom: '16px', 
                padding: '12px',
                backgroundColor: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '4px',
                color: '#155724'
            }}>
                <strong>Zusammenfassung:</strong> {filteredEntries.length} Käufer
                {searchQuery && ` (gefiltert von ${processedEntries.length})`}
                {' | '}
                <strong>Gekaufte Tickets:</strong> {totals.totalPurchased}
                {' | '}
                <strong>Tickets im Lostopf:</strong> {totals.totalInLottery}
            </div>

            {/* Table */}
            {filteredEntries.length === 0 ? (
                <div style={{ 
                    padding: '40px', 
                    textAlign: 'center', 
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    color: '#6c757d'
                }}>
                    <p>Keine Einträge gefunden{searchQuery && ` für "${searchQuery}"`}.</p>
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                    }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                <th style={{ 
                                    padding: '12px 8px', 
                                    borderBottom: '2px solid #dee2e6',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    #
                                </th>
                                <th style={{ 
                                    padding: '12px 8px', 
                                    borderBottom: '2px solid #dee2e6',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    Name
                                </th>
                                <th style={{ 
                                    padding: '12px 8px', 
                                    borderBottom: '2px solid #dee2e6',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    E-Mail
                                </th>
                                <th style={{ 
                                    padding: '12px 8px', 
                                    borderBottom: '2px solid #dee2e6',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    Gekaufte Tickets
                                </th>
                                <th style={{ 
                                    padding: '12px 8px', 
                                    borderBottom: '2px solid #dee2e6',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    Tickets im Lostopf
                                </th>
                                <th style={{ 
                                    padding: '12px 8px', 
                                    borderBottom: '2px solid #dee2e6',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    Losnummern
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEntries.map((entry: BuyerEntryWithLottery, index: number) => (
                                <tr key={entry.email || entry.id || index} style={{
                                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                                }}>
                                    <td style={{ 
                                        padding: '10px 8px', 
                                        borderBottom: '1px solid #dee2e6',
                                        color: '#6c757d'
                                    }}>
                                        {index + 1}
                                    </td>
                                    <td style={{ 
                                        padding: '10px 8px', 
                                        borderBottom: '1px solid #dee2e6',
                                        maxWidth: '200px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {entry.name || '—'}
                                    </td>
                                    <td style={{ 
                                        padding: '10px 8px', 
                                        borderBottom: '1px solid #dee2e6',
                                        maxWidth: '250px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {entry.email || '—'}
                                    </td>
                                    <td style={{ 
                                        padding: '10px 8px', 
                                        borderBottom: '1px solid #dee2e6',
                                        textAlign: 'right'
                                    }}>
                                        {entry.ticketsPurchased}
                                    </td>
                                    <td style={{ 
                                        padding: '10px 8px', 
                                        borderBottom: '1px solid #dee2e6',
                                        textAlign: 'right',
                                        fontWeight: entry.ticketsInLottery > entry.ticketsPurchased ? 600 : 400,
                                        color: entry.ticketsInLottery > entry.ticketsPurchased ? '#28a745' : 'inherit'
                                    }}>
                                        {entry.ticketsInLottery}
                                        {entry.ticketsInLottery > entry.ticketsPurchased && (
                                            <span style={{ 
                                                marginLeft: '4px',
                                                fontSize: '12px',
                                                color: '#28a745'
                                            }}>
                                                (+{entry.ticketsInLottery - entry.ticketsPurchased})
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ 
                                        padding: '10px 8px', 
                                        borderBottom: '1px solid #dee2e6',
                                        textAlign: 'left'
                                    }}>
                                        {entry.ticketNumberRange}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr style={{ backgroundColor: '#e9ecef', fontWeight: 600 }}>
                                <td colSpan={3} style={{ 
                                    padding: '12px 8px', 
                                    borderTop: '2px solid #dee2e6',
                                    textAlign: 'right'
                                }}>
                                    Summe:
                                </td>
                                <td style={{ 
                                    padding: '12px 8px', 
                                    borderTop: '2px solid #dee2e6',
                                    textAlign: 'right'
                                }}>
                                    {totals.totalPurchased}
                                </td>
                                <td style={{ 
                                    padding: '12px 8px', 
                                    borderTop: '2px solid #dee2e6',
                                    textAlign: 'right',
                                    color: '#28a745'
                                }}>
                                    {totals.totalInLottery}
                                </td>
                                <td style={{ 
                                    padding: '12px 8px', 
                                    borderTop: '2px solid #dee2e6'
                                }}>
                                    {/* Empty cell for Losnummern column - no total applicable */}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TicketsSichten;
